import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import recoupLogo from '@/assets/recoup-logo.png';

// Handles the provider redirect (e.g. /auth/google?code=...), exchanges the
// authorization code on our backend, stores the Recoup JWT and lands the user.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function OAuthCallback({ provider = 'google' }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard against StrictMode double-invoke (code is single-use)
    ran.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const providerError = params.get('error');
    if (providerError) {
      toast.error('Sign-in was cancelled');
      navigate('/login', { replace: true });
      return;
    }
    if (!code) {
      navigate('/login', { replace: true });
      return;
    }
    const redirect_uri = window.location.origin + `/auth/${provider}`;
    api.post(`/auth/${provider}`, { code, redirect_uri })
      .then((res) => {
        localStorage.setItem('cocc_token', res.data.token);
        localStorage.setItem('cocc_user', JSON.stringify(res.data.user));
        setUser(res.data.user);
        toast.success('Signed in');
        navigate('/', { replace: true });
      })
      .catch((e) => {
        const msg = e?.response?.data?.detail || 'Sign-in failed. Please try again.';
        setError(msg);
        toast.error(msg);
        setTimeout(() => navigate('/login', { replace: true }), 2800);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F6F7F9] gap-4 px-6">
      <img src={recoupLogo} alt="Recoup" className="h-9 w-auto opacity-90" draggable="false" />
      {!error ? (
        <div className="flex items-center gap-2.5 text-slate-500" data-testid="oauth-finishing">
          <Loader2 className="h-5 w-5 animate-spin text-brand" />
          <span className="text-sm">Finishing sign-in&hellip;</span>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 text-center max-w-sm" data-testid="oauth-error">
          <div className="rounded-xl bg-danger-50 ring-1 ring-danger-100 p-2.5"><AlertTriangle className="h-5 w-5 text-danger-700" /></div>
          <p className="text-sm font-semibold text-ink-900">Could not sign you in</p>
          <p className="text-xs text-slate-500">{error}</p>
          <p className="text-[11px] text-slate-400 mt-1">Redirecting you back to the login page&hellip;</p>
        </div>
      )}
    </div>
  );
}
